// SpecializedJobs.jsx
import React from 'react';
import { Wrench, DollarSign, Heart, Shield, Briefcase, GraduationCap } from 'lucide-react';
import './SpecializedJobs.css';

const SpecializedJobs = ({ data, onChange, validationErrors = {} }) => {
  const handleMultiSelect = (field, value) => {
    const currentValues = data[field] || [];
    const newValues = currentValues.includes(value)
      ? currentValues.filter(v => v !== value)
      : [...currentValues, value];
    onChange(field, newValues);
  };

  const handleJobPositionChange = (index, value) => {
    const positions = data.specializedPositions || Array(7).fill('');
    positions[index] = value;
    onChange('specializedPositions', positions);
  };

  const handleOtherFieldChange = (field, value) => {
    onChange(field, value);
  };

  // Helper function to get error class
  const getErrorClass = (fieldName) => {
    return validationErrors[fieldName] ? 'error' : '';
  };

  // Helper function to render error message
  const renderError = (fieldName) => {
    if (validationErrors[fieldName]) {
      return (
        <div className="error-message" role="alert">
          {validationErrors[fieldName]}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="step-content">
      <div className="step-header-section">
        {/* <Wrench className="section-icon" size={32} /> */}
        <h2>رابعاً: الوظائف التخصصية والبيانات الشخصية المطلوبة</h2>
      </div>
      
      {/* Specialized Positions List */}
      <div className="">
        <div className="question-header">
          <span className="question-number">14</span>
          <label className="question-title">اذكر مسمى الوظائف التخصصية الموجودة في شركتكم؟</label>
        </div>
        
        <div className="data-category">
          {/* <div className="category-header">
            <Wrench className="category-icon" size={24} />
            <h4>الوظائف التخصصية:</h4>
          </div> */}
          
          <div className="positions-grid">
            {[0, 1, 2, 3, 4, 5, 6].map(index => (
              <div key={index} className="position-input-group specialized-position">
                <label className="position-label" htmlFor={`specialized-position-${index}`}>
                  الوظيفة {String(index + 1).padStart(2, '0')}:
                </label>
                <input
                  id={`specialized-position-${index}`}
                  type="text"
                  value={(data.specializedPositions || [])[index] || ''}
                  onChange={(e) => handleJobPositionChange(index, e.target.value)}
                  placeholder="(مثل: مهندس برمجيات، محاسب قانوني، طبيب...)"
                  className="position-input"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Personal Data Requirements */}
      <div className="">
        <div className="question-header">
          <span className="question-number">15</span>
          <label className="question-title">
            حدد البيانات الشخصية التي تطلبها شركتكم من المترشحين للتعيين على الوظائف التخصصية؟ 
            <span className="required-indicator">*</span>
            <span className="multiple-choice-note">(يمكن اختيار أكثر من إجابة)</span>
          </label>
        </div>
        
        {/* Financial Data Section */}
        <div className="data-category" data-field="specializedFinancialData">
          <div className="category-header">
            <DollarSign className="category-icon" size={24} />
            <h4>البيانات المالية:</h4>
          </div>
          
          <div className={`checkbox-grid ${getErrorClass('specializedFinancialData')}`}>
            {[
              'الراتب السابق',
              'الحالة الائتمانية',
              'حسابات البنوك',
              'بيانات التأمينات الاجتماعية',
              'مصادر الدخل الأخرى',
              'الالتزامات المالية والديون',
              'غير موجود'
            ].map(item => (
              <label key={item} className="checkbox-item financial-item">
                <input
                  type="checkbox"
                  checked={(data.specializedFinancialData || []).includes(item)}
                  onChange={() => handleMultiSelect('specializedFinancialData', item)}
                />
                <span className="checkbox-custom" aria-hidden="true"></span>
                <span className="checkbox-text">{item}</span>
              </label>
            ))}
          </div>
          {renderError('specializedFinancialData')}
          
          <div className="other-input-group">
            <label className="other-label" htmlFor="other-specialized-financial">
              أخرى (يرجى ذكرها):
            </label>
            <input
              id="other-specialized-financial"
              type="text"
              value={data.otherSpecializedFinancialData || ''}
              onChange={(e) => handleOtherFieldChange('otherSpecializedFinancialData', e.target.value)}
              placeholder="بيانات مالية أخرى للوظائف التخصصية..."
              className={`other-input ${getErrorClass('otherSpecializedFinancialData')}`}
            />
            {renderError('otherSpecializedFinancialData')}
          </div>
        </div>

        {/* Health Data Section */}
        <div className="data-category" data-field="specializedHealthData">
          <div className="category-header">
            <Heart className="category-icon" size={24} />
            <h4>البيانات الصحية:</h4>
          </div>
          
          <div className={`checkbox-grid ${getErrorClass('specializedHealthData')}`}>
            {[
              'الفحوصات الطبية العامة',
              'فحوصات طبية متخصصة',
              'تاريخ الأمراض المزمنة',
              'فحص السموم',
              'غير موجود'
            ].map(item => (
              <label key={item} className="checkbox-item health-item">
                <input
                  type="checkbox"
                  checked={(data.specializedHealthData || []).includes(item)}
                  onChange={() => handleMultiSelect('specializedHealthData', item)}
                />
                <span className="checkbox-custom" aria-hidden="true"></span>
                <span className="checkbox-text">{item}</span>
              </label>
            ))}
          </div>
          {renderError('specializedHealthData')}
          
          <div className="other-input-group">
            <label className="other-label" htmlFor="other-specialized-health">
              أخرى (يرجى ذكرها):
            </label>
            <input
              id="other-specialized-health"
              type="text"
              value={data.otherSpecializedHealthData || ''}
              onChange={(e) => handleOtherFieldChange('otherSpecializedHealthData', e.target.value)}
              placeholder="بيانات صحية أخرى للوظائف التخصصية..."
              className={`other-input ${getErrorClass('otherSpecializedHealthData')}`}
            />
            {renderError('otherSpecializedHealthData')}
          </div>
        </div>

        {/* Legal and Security Data Section */}
        <div className="data-category" data-field="specializedLegalData">
          <div className="category-header">
            <Shield className="category-icon" size={24} />
            <h4>البيانات القانونية والأمنية:</h4>
          </div>
          
          <div className={`checkbox-grid ${getErrorClass('specializedLegalData')}`}>
            {[
              'قضايا قانونية سابقة',
              'تصاريح أمنية سابقة',
              'سجل القيادة',
              'السجل الجنائي المفصل',
              'التحقيقات الأمنية',
              'سجل السفر',
              'غير موجود'
            ].map(item => (
              <label key={item} className="checkbox-item security-item">
                <input
                  type="checkbox"
                  checked={(data.specializedLegalData || []).includes(item)}
                  onChange={() => handleMultiSelect('specializedLegalData', item)}
                />
                <span className="checkbox-custom" aria-hidden="true"></span>
                <span className="checkbox-text">{item}</span>
              </label>
            ))}
          </div>
          {renderError('specializedLegalData')}
          
          <div className="other-input-group">
            <label className="other-label" htmlFor="other-specialized-legal">
              أخرى (يرجى ذكرها):
            </label>
            <input
              id="other-specialized-legal"
              type="text"
              value={data.otherSpecializedLegalData || ''}
              onChange={(e) => handleOtherFieldChange('otherSpecializedLegalData', e.target.value)}
              placeholder="بيانات قانونية وأمنية أخرى للوظائف التخصصية..."
              className={`other-input ${getErrorClass('otherSpecializedLegalData')}`}
            />
            {renderError('otherSpecializedLegalData')}
          </div>
        </div>

        {/* Advanced Professional Data Section */}
        <div className="data-category" data-field="specializedProfessionalData">
          <div className="category-header">
            <Briefcase className="category-icon" size={24} />
            <h4>البيانات المهنية المتقدمة:</h4>
          </div>
          
          <div className={`checkbox-grid ${getErrorClass('specializedProfessionalData')}`}>
            {[
              'خطابات التوصية',
              'تقييمات الأداء السابقة',
              'أسباب ترك الوظائف',
              'غير موجود'
            ].map(item => (
              <label key={item} className="checkbox-item professional-item">
                <input
                  type="checkbox"
                  checked={(data.specializedProfessionalData || []).includes(item)}
                  onChange={() => handleMultiSelect('specializedProfessionalData', item)}
                />
                <span className="checkbox-custom" aria-hidden="true"></span>
                <span className="checkbox-text">{item}</span>
              </label>
            ))}
          </div>
          {renderError('specializedProfessionalData')}
          
          <div className="other-input-group">
            <label className="other-label" htmlFor="other-specialized-professional">
              أخرى (يرجى ذكرها):
            </label>
            <input
              id="other-specialized-professional"
              type="text"
              value={data.otherSpecializedProfessionalData || ''}
              onChange={(e) => handleOtherFieldChange('otherSpecializedProfessionalData', e.target.value)}
              placeholder="بيانات مهنية أخرى للوظائف التخصصية..."
              className={`other-input ${getErrorClass('otherSpecializedProfessionalData')}`}
            />
            {renderError('otherSpecializedProfessionalData')}
          </div>
        </div>

        {/* Academic and Professional Data Section */}
        <div className="data-category" data-field="specializedAcademicData">
          <div className="category-header">
            <GraduationCap className="category-icon" size={24} />
            <h4>البيانات الأكاديمية والمهنية:</h4>
          </div>
          
          <div className={`checkbox-grid ${getErrorClass('specializedAcademicData')}`}>
            {[
              'المؤهلات الأكاديمية المتخصصة',
              'المؤهلات المهنية المعتمدة',
              'الخبرات التخصصية السابقة',
              'المشاريع والإنجازات',
              'التراخيص المهنية',
              'غير موجود'
            ].map(item => (
              <label key={item} className="checkbox-item academic-item">
                <input
                  type="checkbox"
                  checked={(data.specializedAcademicData || []).includes(item)}
                  onChange={() => handleMultiSelect('specializedAcademicData', item)}
                />
                <span className="checkbox-custom" aria-hidden="true"></span>
                <span className="checkbox-text">{item}</span>
              </label>
            ))}
          </div>
          {renderError('specializedAcademicData')}
          
          <div className="other-input-group">
            <label className="other-label" htmlFor="other-specialized-academic">
              أخرى (يرجى ذكرها):
            </label>
            <input
              id="other-specialized-academic"
              type="text"
              value={data.otherSpecializedAcademicData || ''}
              onChange={(e) => handleOtherFieldChange('otherSpecializedAcademicData', e.target.value)}
              placeholder="بيانات أكاديمية ومهنية أخرى للوظائف التخصصية..."
              className={`other-input ${getErrorClass('otherSpecializedAcademicData')}`}
            />
            {renderError('otherSpecializedAcademicData')}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpecializedJobs;